import React, { useState } from "react";
import "./LoginPage.css";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

type AuthView = "login" | "signup" | "confirm" | "forgot" | "resetCode" | "newPassword";

export function LoginPage() {
  const [view, setView] = useState<AuthView>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn, newPasswordRequired, completeNewPassword } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signIn(email, password);
      if (!newPasswordRequired) navigate("/dashboard");
    } catch (err) {
      console.error("Login failed:", err);
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await completeNewPassword(newPassword);
      navigate("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) { setError("Passwords do not match"); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      const { CognitoUserPool, CognitoUserAttribute } = await import("amazon-cognito-identity-js");
      const pool = new CognitoUserPool({
        UserPoolId: import.meta.env.VITE_USER_POOL_ID,
        ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      });
      const attributes = [
        new CognitoUserAttribute({ Name: "email", Value: email }),
        new CognitoUserAttribute({ Name: "given_name", Value: name.split(" ")[0] }),
        new CognitoUserAttribute({ Name: "family_name", Value: name.split(" ").slice(1).join(" ") || name }),
      ];
      await new Promise<void>((resolve, reject) => {
        pool.signUp(email, password, attributes, [], (err) => {
          if (err) reject(err); else resolve();
        });
      });
      setMessage("Check your email for a verification code.");
      setView("confirm");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { CognitoUserPool, CognitoUser } = await import("amazon-cognito-identity-js");
      const pool = new CognitoUserPool({
        UserPoolId: import.meta.env.VITE_USER_POOL_ID,
        ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      });
      const user = new CognitoUser({ Username: email, Pool: pool });
      await new Promise<void>((resolve, reject) => {
        user.confirmRegistration(code, true, (err) => {
          if (err) reject(err); else resolve();
        });
      });
      setMessage("Email verified! You can now sign in.");
      setView("login");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { CognitoUserPool, CognitoUser } = await import("amazon-cognito-identity-js");
      const pool = new CognitoUserPool({
        UserPoolId: import.meta.env.VITE_USER_POOL_ID,
        ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      });
      const user = new CognitoUser({ Username: email, Pool: pool });
      await new Promise<void>((resolve, reject) => {
        user.forgotPassword({
          onSuccess: () => resolve(),
          onFailure: (err) => reject(err),
          inputVerificationCode: () => resolve(),
        });
      });
      setMessage("Check your email for a reset code.");
      setView("resetCode");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send reset code");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      const { CognitoUserPool, CognitoUser } = await import("amazon-cognito-identity-js");
      const pool = new CognitoUserPool({
        UserPoolId: import.meta.env.VITE_USER_POOL_ID,
        ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
      });
      const user = new CognitoUser({ Username: email, Pool: pool });
      await new Promise<void>((resolve, reject) => {
        user.confirmPassword(code, newPassword, {
          onSuccess: () => resolve(),
          onFailure: (err) => reject(err),
        });
      });
      setMessage("Password reset! You can now sign in.");
      setView("login");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => {
    if (newPasswordRequired) {
      return (
        <form className="protrain-login-form" onSubmit={handleNewPassword}>
          <p className="protrain-login-hint">Please set a new password to continue.</p>
          {error && <div className="protrain-login-error" role="alert">{error}</div>}
          <label className="protrain-login-label" htmlFor="new-pw">New Password</label>
          <input id="new-pw" className="protrain-login-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required autoComplete="new-password" />
          <label className="protrain-login-label" htmlFor="confirm-pw">Confirm Password</label>
          <input id="confirm-pw" className="protrain-login-input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required autoComplete="new-password" />
          <button className="protrain-login-submit" type="submit" disabled={loading}>{loading ? "Setting..." : "Set New Password"}</button>
        </form>
      );
    }

    switch (view) {
      case "signup":
        return (
          <form className="protrain-login-form" onSubmit={handleSignup}>
            {error && <div className="protrain-login-error" role="alert">{error}</div>}
            {message && <div className="protrain-login-message">{message}</div>}
            <label className="protrain-login-label" htmlFor="signup-name">Full Name</label>
            <input id="signup-name" className="protrain-login-input" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
            <label className="protrain-login-label" htmlFor="signup-email">Email</label>
            <input id="signup-email" className="protrain-login-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            <label className="protrain-login-label" htmlFor="signup-pw">Password</label>
            <input id="signup-pw" className="protrain-login-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" />
            <label className="protrain-login-label" htmlFor="signup-confirm">Confirm Password</label>
            <input id="signup-confirm" className="protrain-login-input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required autoComplete="new-password" />
            <button className="protrain-login-submit" type="submit" disabled={loading}>{loading ? "Creating account..." : "Sign Up"}</button>
            <button type="button" className="protrain-login-link" onClick={() => { setView("login"); setError(""); setMessage(""); }}>Already have an account? Sign in</button>
          </form>
        );

      case "confirm":
        return (
          <form className="protrain-login-form" onSubmit={handleConfirm}>
            {error && <div className="protrain-login-error" role="alert">{error}</div>}
            {message && <div className="protrain-login-message">{message}</div>}
            <label className="protrain-login-label" htmlFor="confirm-code">Verification Code</label>
            <input id="confirm-code" className="protrain-login-input" value={code} onChange={(e) => setCode(e.target.value)} required placeholder="Enter code from email" />
            <button className="protrain-login-submit" type="submit" disabled={loading}>{loading ? "Verifying..." : "Verify Email"}</button>
            <button type="button" className="protrain-login-link" onClick={() => { setView("login"); setError(""); setMessage(""); }}>Back to sign in</button>
          </form>
        );

      case "forgot":
        return (
          <form className="protrain-login-form" onSubmit={handleForgotPassword}>
            {error && <div className="protrain-login-error" role="alert">{error}</div>}
            <p className="protrain-login-hint">Enter your email to receive a password reset code.</p>
            <label className="protrain-login-label" htmlFor="forgot-email">Email</label>
            <input id="forgot-email" className="protrain-login-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            <button className="protrain-login-submit" type="submit" disabled={loading}>{loading ? "Sending..." : "Send Reset Code"}</button>
            <button type="button" className="protrain-login-link" onClick={() => { setView("login"); setError(""); }}>Back to sign in</button>
          </form>
        );

      case "resetCode":
        return (
          <form className="protrain-login-form" onSubmit={handleResetPassword}>
            {error && <div className="protrain-login-error" role="alert">{error}</div>}
            {message && <div className="protrain-login-message">{message}</div>}
            <label className="protrain-login-label" htmlFor="reset-code">Reset Code</label>
            <input id="reset-code" className="protrain-login-input" value={code} onChange={(e) => setCode(e.target.value)} required placeholder="Enter code from email" />
            <label className="protrain-login-label" htmlFor="reset-pw">New Password</label>
            <input id="reset-pw" className="protrain-login-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required autoComplete="new-password" />
            <label className="protrain-login-label" htmlFor="reset-confirm">Confirm Password</label>
            <input id="reset-confirm" className="protrain-login-input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required autoComplete="new-password" />
            <button className="protrain-login-submit" type="submit" disabled={loading}>{loading ? "Resetting..." : "Reset Password"}</button>
            <button type="button" className="protrain-login-link" onClick={() => { setView("login"); setError(""); setMessage(""); }}>Back to sign in</button>
          </form>
        );

      default: // login
        return (
          <form className="protrain-login-form" onSubmit={handleLogin}>
            {error && <div className="protrain-login-error" role="alert">{error}</div>}
            {message && <div className="protrain-login-message">{message}</div>}
            <label className="protrain-login-label" htmlFor="email">Email</label>
            <input id="email" className="protrain-login-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
            <label className="protrain-login-label" htmlFor="password">Password</label>
            <input id="password" className="protrain-login-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
            <button className="protrain-login-submit" type="submit" disabled={loading}>{loading ? "Signing in..." : "Sign In"}</button>
            <div className="protrain-login-links">
              <button type="button" className="protrain-login-link" onClick={() => { setView("signup"); setError(""); setMessage(""); }}>Create an account</button>
              <button type="button" className="protrain-login-link" onClick={() => { setView("forgot"); setError(""); setMessage(""); }}>Forgot password?</button>
            </div>
          </form>
        );
    }
  };

  return (
    <div className="protrain-login">
      <div className="protrain-login-card">
        <div className="protrain-login-header">
          <span className="protrain-login-logo">☁</span>
          <h1 className="protrain-login-title">nimbus value</h1>
          <p className="protrain-login-subtitle">ProTrain</p>
        </div>
        {renderForm()}
      </div>
    </div>
  );
}
